import { ExportDropdown } from "@components/common/export/export-dropdown";
import { Markup } from "@custom-types/project-markups-types";
import { ProjectLaunchTarget } from "@custom-types/sdb-company-types";
import { useDateTime } from "@hooks/use-date-time";
import { useMembers } from "@hooks/use-members";
import { defaultProjectLaunchTargetSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { downloadMarkupData, ExportFormat } from "@utils/file-data-export";
import { TableType } from "@utils/track-event/track-event-list";
import { useCallback } from "react";

interface Props {
  /** List of selected markups in table */
  selectedMarkups: Markup[];

  /** The name of the markup's project */
  projectName: string;

  /** True if some entities are selected */
  areEntitiesSelected: boolean;
}

/** Dropdown to export markups in the file format selected by the user */
export function BulkExportMarkups({
  selectedMarkups,
  projectName,
  areEntitiesSelected,
}: Props): JSX.Element {
  const { companyMembers, projectMembers } = useMembers();
  const projectLaunchSettings = useAppSelector(
    defaultProjectLaunchTargetSelector
  );
  const { formatDate } = useDateTime();

  /** Returns the url of the markup opening either in Viewer or WebEditor */
  const markupHref = useCallback(
    (markup: Markup) => {
      let openTarget = ProjectLaunchTarget.sphereViewer;
      if (
        projectLaunchSettings.defaultOpenProjectTarget ===
          ProjectLaunchTarget.webEditor &&
        markup?.webEditorUrl
      ) {
        openTarget = projectLaunchSettings.defaultOpenProjectTarget;
      }

      return openTarget === ProjectLaunchTarget.sphereViewer
        ? markup.sphereViewerUrl
        : markup.webEditorUrl;
    },
    [projectLaunchSettings.defaultOpenProjectTarget]
  );

  const exportHandler = useCallback(
    async (format: ExportFormat): Promise<number> => {
      return await downloadMarkupData({
        markups: selectedMarkups,
        companyMembers,
        projectMembers,
        fileName: `${projectName}-Annotation List`,
        markupHref,
        formatDate,
        format,
      });
    },
    [selectedMarkups, companyMembers, projectMembers, projectName, markupHref, formatDate]
  );

  return (
    <ExportDropdown
      trackingProps={{
        dataType: TableType.projectMarkups,
        numberOfEntities: selectedMarkups.length,
        areEntitiesSelected,
      }}
      exportHandler={exportHandler}
      toastMessage={{
        success: "Successfully exported annotations.",
        error: "Cannot export annotations. Please try again.",
      }}
      buttonTooltipText="Export annotations"
    />
  );
}
